'use client';
import { createPortal } from 'react-dom';
import { Icon } from './Icon';
import { useLayerLock } from '../lib/useLayerLock';

/**
 * 작업 중 표시 — **점 셋**(2026-08-28 사용자 확정, /ux-lab §17 에서 고름).
 *
 * 모양은 하나로 고정한다. 갈래마다 다른 모양이 나오면 그게 다시 흩어짐이 된다.
 * 쓰는 자리만 둘로 나뉜다 —
 *
 *  · **덮개형**(`<Working overlay note="…" />`) — 결제처럼 되돌릴 수 없는 처리.
 *    화면을 덮고 스크롤을 잠그며 **닫는 길을 두지 않는다**.
 *  · **영역형**(`<Working block />`) — 목록·상세를 불러오는 중. 그 자리에서 기다린다.
 *    안내문구는 붙여도 되고 빼도 된다(목록 이어받기처럼 짧은 자리는 뺀다).
 *  · **버튼형**(`<Working inline />`) — 누른 버튼이 일하는 동안 그 안에서 돈다.
 *
 * **덮개형은 안내문구가 필수다**(타입으로 강제 — 아래 `WorkingProps` 참고).
 * 화면이 덮인 채 몇 초가 흐르면 사용자는 창을 닫거나 뒤로 가려 하는데, 결제 도중에는
 * 그것이 사고로 이어진다. 규칙을 주석에만 적으면 새 화면에서 빠뜨리므로 컴파일이 막게 했다.
 *
 * 움직임 줄이기(§7)를 켠 분께는 전역 규칙이 애니메이션을 멈춘다 — 그때는 점이 튀지 않고
 * 흐린 채로 멈춰 서지만, 진행 중이라는 사실은 문구와 `aria-busy` 가 계속 전한다.
 */

type WorkingProps =
  /** 덮개형 — 되돌릴 수 없는 처리. `note` 없이는 쓸 수 없다 */
  | { overlay: true; message: string; note: string; block?: never; inline?: never }
  /** 영역형 — 자리 안에서 기다린다(목록·상세를 불러오는 중). `note` 는 있어도 없어도 된다 */
  | { block: true; message: string; note?: string; overlay?: never; inline?: never }
  /** 버튼형 — 버튼 글자 옆에서 돈다. 문구는 버튼이 이미 갖고 있다 */
  | { inline: true; overlay?: never; block?: never; message?: never; note?: never };

export function Working(props: WorkingProps) {
  // 덮개형만 스크롤을 잠근다. 훅은 언제나 같은 순서로 불려야 하므로 분기 앞에 둔다
  useLayerLock(!!props.overlay);

  if (props.inline) return <Dots inline />;

  const body = (
    <>
      <Dots />
      <p className="working-msg">{props.message}</p>
      {props.note && <p className="working-note"><Icon name={ICON_NOTE} size={13} /> {props.note}</p>}
    </>
  );

  // 영역형은 그 자리에 그대로 그린다 — 덮을 것이 없고, 스크롤도 막지 않는다
  if (props.block) {
    return <div className="working-block" role="status" aria-live="polite" aria-busy="true">{body}</div>;
  }

  return createPortal(
    <div className="working-overlay" role="status" aria-live="polite" aria-busy="true">
      <div className="working-card">{body}</div>
    </div>,
    document.body,
  );
}

/** 아이콘 이름은 태그 밖 상수로 — check-icons 가 문자열을 전수 검사한다 */
const ICON_NOTE = 'info';

/** 점 셋 — CSS 로만 움직인다(라이브러리 없음) */
function Dots({ inline = false }: { inline?: boolean }) {
  return (
    <span className={`working-dots${inline ? ' is-inline' : ''}`} aria-hidden="true">
      <i /><i /><i />
    </span>
  );
}
